import type {ShoppingCartOrderLine, SubmitOrderLine} from 'dummen-shared/src/interfaces/order.line.type.ts';
import type {Substitute} from 'dummen-shared';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';

dayjs.extend(weekOfYear);

export const groupAndSortByConsignment = (orderLines: ShoppingCartOrderLine[]): Record<string, ShoppingCartOrderLine[]> => {
	const orderLinesByConsignment: Record<string, ShoppingCartOrderLine[]> = {};

	for (const orderLine of orderLines) {
		const groupKey = orderLine.consignment || '';
		if (!orderLinesByConsignment[groupKey]) {
			orderLinesByConsignment[groupKey] = [];
		}
		orderLinesByConsignment[groupKey].push(orderLine);
	}

	// Sort by consignment, empty consignment last
	const sortedByConsignment: Record<string, ShoppingCartOrderLine[]> = {};

	for (const key of Object.keys(orderLinesByConsignment).sort((a, b) => {
		if (a === '') return 1;
		if (b === '') return -1;
		return a.localeCompare(b);
	})) {
		sortedByConsignment[key] = orderLinesByConsignment[key];
	}

	return sortedByConsignment;
};

export const getFormattedRegionName = (region: string): string => {
	const formattedRegion = region.replaceAll('_', '%20');
	return formattedRegion.toLowerCase();
};

const _getDetailUrl = (region: string, urlFriendlyProductGroup: string, urlFriendlyName: string, urlFriendlyId: string): string | undefined => {
	if (!region || !urlFriendlyProductGroup || !urlFriendlyName || !urlFriendlyId) {
		return undefined;
	}

	const formattedRegion = getFormattedRegionName(region);
	return `${window.productDetailBaseUrlWithoutRegion}/${formattedRegion}/${urlFriendlyProductGroup}/${urlFriendlyName}/${urlFriendlyId}`;
};

export const getProductDetailUrl = (orderLine: ShoppingCartOrderLine): string | undefined => {
	if (orderLine.notExistingVariety) {
		return undefined;
	}

	return _getDetailUrl(orderLine.region, orderLine.urlFriendlyProductgroup, orderLine.urlFriendlyName, orderLine.urlFriendlyId);
};

export const getProductDetailUrlForSubstitute = (substitute: Substitute, region: string): string | undefined => {
	return _getDetailUrl(region, substitute.urlFriendlyProductgroup, substitute.urlFriendlyName, substitute.urlFriendlyId);
};

export const mapToSubmitOrderLine = (orderLine: ShoppingCartOrderLine,
                                     preferredDeliveryDay: number | null): SubmitOrderLine => {
	return {
		formId: orderLine.formId,
		articleId: orderLine.articleId,
		locationId: orderLine.locationId,
		consignment: orderLine.consignment ?? '',
		week: orderLine.week,
		year: orderLine.year,
		preferredDeliveryDay: preferredDeliveryDay,
		amount: orderLine.amount,
		packingUnit: orderLine.packingUnit
	};
};

/**
 * Determines if a delivery date is considered surplus (i.e. ordered for delivery within the next two weeks).
 *
 * @param deliveryWeek the delivery week of the order line.
 * @param deliveryYear the delivery year of the order line.
 * @returns True if the order line is surplus, false otherwise.
 */
export const orderLineIsSurplus = (deliveryWeek: number, deliveryYear: number): boolean => {
	// TODO: This filter was implemented to solve PAT availability check issues with surplus order lines. Disabled until further notice.
	//  See https://eleven-nl.atlassian.net/browse/CPXDUM-923

	//const surplusDate = dayjs().add(2, 'week');
	//const surplusWeek = surplusDate.week();
	//const surplusYear = surplusDate.year();

	//return (deliveryYear < surplusYear) || (deliveryYear === surplusYear && deliveryWeek <= surplusWeek);

	return false;
};
